import { UiState } from "instantsearch.js";
import { MeiliSearchResponse } from "../hooks/meilisearch/useAutocomplete/props";
import { meiliSearchClient } from "../meiliseach/client";
import { normalizeString } from "./common";

type CustomUiState = UiState & {
  vehicles?: {
    refinementList?: {
      [key: string]: string[];
    };
    range?: {
      [key: string]: string;
    };
  };
};

let facetCache: Record<string, string[]> | null = null;

export const getFacetValuesSync = (): Record<string, string[]> => {
  if (!facetCache) {
    throw new Error(
      "Facet values not loaded. Ensure loadFacetValues() is called before rendering."
    );
  }
  return facetCache;
};

export const loadFacetValues = async () => {
  if (facetCache) return facetCache;

  const result: MeiliSearchResponse = await meiliSearchClient
    .index("vehicles")
    .search("", {
      facets: [
        "kilometers",
        "price",
        "city",
        "model",
        "fuel",
        "category",
        "make",
        "model",
      ],
      limit: 0,
    });

  facetCache = Object.fromEntries(
    Object.entries(result.facetDistribution || {}).map(([key, values]) => [
      key,
      Object.keys(values),
    ])
  );

  return facetCache;
};

const attributeMapping: { [key: string]: string } = {
  make: "marca",
  model: "modelo",
  category: "categoria",
  fuel: "combustivel",
  color: "cor",
  transmission: "transmissao",
  city: "cidade",
  state: "estado",
  kilometers: "Km",
  price: "Preco",
  style: "Estilo",
};

export const mapAttributeToPortuguese = (attribute: string): string => {
  return attributeMapping[attribute] || attribute;
};

export const stateToUrl = (
  mapAttributeToPortuguese: (key: string) => string
) => {
  return (uiState: CustomUiState) => {
    const refinementList = uiState.vehicles?.refinementList || {};
    const range = uiState.vehicles?.range || {};

    const refinementParams = Object.keys(refinementList).reduce(
      (acc: { [key: string]: string }, key: string) => {
        const mappedKey = mapAttributeToPortuguese(key);
        acc[mappedKey] = refinementList[key]
          .join("-e-")
          .replace(/ /g, "-")
          .toLowerCase();
        return acc;
      },
      {}
    );

    const rangeParams = Object.keys(range).reduce(
      (acc: { [key: string]: string }, key: string) => {
        const mappedKey = mapAttributeToPortuguese(key);
        const rangeValue = range[key]; // Valor de range é no formato "10000:20000"

        if (rangeValue) {
          const [min, max] = rangeValue
            .split(":")
            .map((str) => parseInt(str, 10)); // Divide a string em min e max
          if (!isNaN(min)) acc[`min${mappedKey}`] = String(min);
          if (!isNaN(max)) acc[`max${mappedKey}`] = String(max);
        }

        return acc;
      },
      {}
    );

    return { ...refinementParams, ...rangeParams };
  };
};

const reverseAttributeMapping: Record<string, string> = Object.entries(
  attributeMapping
).reduce((acc: Record<string, string>, [key, value]: [string, string]) => {
  acc[value] = key;
  return acc;
}, {});

export const urlToState = (
  routeState: Record<string, string>
): CustomUiState => {
  const facetValues = getFacetValuesSync();

  const refinementList = Object.keys(routeState).reduce(
    (acc: Record<string, string[]>, key: string) => {
      const originalKey = reverseAttributeMapping[key] || key;

      if (!key.startsWith("min") && !key.startsWith("max")) {
        const rawValues = routeState[key]
          .split("-e-")
          .map((item: string) => normalizeString(item.replace(/-/g, " ")));

        // Corrigir valores com base nas facetas pré-carregadas
        acc[originalKey] = rawValues.map((value) => {
          const realValue = facetValues[originalKey]?.find(
            (facetValue) => normalizeString(facetValue) === value
          );
          return realValue || value;
        });
      }

      return acc;
    },
    {}
  );

  const range = Object.keys(routeState).reduce(
    (acc: Record<string, string>, key: string) => {
      if (key.startsWith("min") || key.startsWith("max")) {
        const originalKey = key.replace(/^min|^max/, "").toLowerCase();

        const rangeKey =
          originalKey === "km"
            ? "kilometers"
            : originalKey === "preco"
              ? "price"
              : undefined;

        if (rangeKey) {
          const minKey = `min${originalKey.charAt(0).toUpperCase() + originalKey.slice(1)}`;
          const maxKey = `max${originalKey.charAt(0).toUpperCase() + originalKey.slice(1)}`;

          const min = routeState[minKey]
            ? parseInt(routeState[minKey], 10)
            : undefined;
          const max = routeState[maxKey]
            ? parseInt(routeState[maxKey], 10)
            : undefined;

          if (min !== undefined || max !== undefined) {
            acc[rangeKey] = `${min || ""}:${max || ""}`;
          }
        }
      }

      return acc;
    },
    {}
  );

  return {
    vehicles: {
      refinementList,
      range,
    },
  };
};
